import { addMinutes, format, isToday, set, startOfDay } from "date-fns";
import { useTranslate } from "ra-core";
import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";

import { cn } from "@/lib/utils";

import type { PersonalEvent } from "../types";
import { getEventColorVar } from "./eventColors";
import { clipToDay, layoutDayEvents } from "./eventLayout";
import { useDateLocale } from "./useDateLocale";

const HOUR_HEIGHT = 48;
const SLOT_MINUTES = 30;
const MIN_EVENT_HEIGHT = 22;
const DEFAULT_SCROLL_HOUR = 7;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

export interface DayTimelineProps {
  day: Date;
  events: PersonalEvent[];
  onCreateAt: (start: Date) => void;
  onSelectEvent: (event: PersonalEvent) => void;
  className?: string;
}

const minuteToPixels = (minute: number) => (minute / 60) * HOUR_HEIGHT;

const useCurrentMinute = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);
  return now.getHours() * 60 + now.getMinutes();
};

export const DayTimeline = ({
  day,
  events,
  onCreateAt,
  onSelectEvent,
  className,
}: DayTimelineProps) => {
  const translate = useTranslate();
  const { dateFnsLocale } = useDateLocale();
  const scrollRef = useRef<HTMLDivElement>(null);
  const currentMinute = useCurrentMinute();
  const showNowLine = isToday(day);

  const positionedEvents = useMemo(
    () =>
      layoutDayEvents(
        events.flatMap((event) => {
          const range = clipToDay(event.start_at, event.end_at, day);
          return range ? [{ ...range, event }] : [];
        }),
      ),
    [events, day],
  );

  // Scroll to the current time (today) or to the morning (other days)
  const dayKey = day.toDateString();
  useEffect(() => {
    if (!scrollRef.current) return;
    const targetMinute = showNowLine
      ? Math.max(0, currentMinute - 60)
      : DEFAULT_SCROLL_HOUR * 60;
    scrollRef.current.scrollTop = minuteToPixels(targetMinute);
    // Only re-scroll when the displayed day changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayKey]);

  const handleGridClick = (clickEvent: MouseEvent<HTMLDivElement>) => {
    const rect = clickEvent.currentTarget.getBoundingClientRect();
    const minute = ((clickEvent.clientY - rect.top) / HOUR_HEIGHT) * 60;
    const slotMinute = Math.floor(minute / SLOT_MINUTES) * SLOT_MINUTES;
    onCreateAt(addMinutes(startOfDay(day), slotMinute));
  };

  const formatTime = (date: Date) =>
    format(date, "p", { locale: dateFnsLocale });

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-semibold first-letter:uppercase">
          {format(day, "EEEE, PPP", { locale: dateFnsLocale })}
        </h3>
        <span className="text-xs text-muted-foreground">
          {translate("crm.personal.timeline.hint")}
        </span>
      </div>
      <div
        ref={scrollRef}
        className="h-[560px] overflow-y-auto rounded-md border"
        data-testid="day-timeline"
      >
        <div className="relative flex" style={{ height: 24 * HOUR_HEIGHT }}>
          <div className="w-16 shrink-0 border-r text-xs text-muted-foreground">
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="pr-2 text-right"
                style={{ height: HOUR_HEIGHT }}
              >
                <span className="relative -top-2">
                  {hour === 0
                    ? ""
                    : formatTime(set(day, { hours: hour, minutes: 0 }))}
                </span>
              </div>
            ))}
          </div>

          <div
            className="relative flex-1 cursor-pointer"
            onClick={handleGridClick}
            role="presentation"
          >
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="border-b border-border/60"
                style={{ height: HOUR_HEIGHT }}
              />
            ))}

            {positionedEvents.map(({ item, column, columns }) => {
              const { event, startMinute, endMinute } = item;
              const color = getEventColorVar(event.color);
              const height = Math.max(
                minuteToPixels(endMinute - startMinute),
                MIN_EVENT_HEIGHT,
              );
              return (
                <button
                  key={event.id}
                  type="button"
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation();
                    onSelectEvent(event);
                  }}
                  className="absolute overflow-hidden rounded-md border-l-4 px-2 py-0.5 text-left text-xs shadow-xs transition-opacity hover:opacity-90"
                  style={{
                    top: minuteToPixels(startMinute),
                    height,
                    left: `calc(${(column / columns) * 100}% + 2px)`,
                    width: `calc(${100 / columns}% - 4px)`,
                    borderLeftColor: color,
                    backgroundColor: `color-mix(in srgb, ${color} 22%, var(--background))`,
                  }}
                >
                  <span className="block truncate font-medium">
                    {event.title}
                  </span>
                  {height > 32 && (
                    <span className="block truncate text-muted-foreground">
                      {formatTime(new Date(event.start_at))} –{" "}
                      {formatTime(new Date(event.end_at))}
                    </span>
                  )}
                </button>
              );
            })}

            {showNowLine && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 z-10 flex items-center"
                style={{ top: minuteToPixels(currentMinute) }}
              >
                <span className="-ml-1 size-2 rounded-full bg-red-500" />
                <span className="h-px flex-1 bg-red-500" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
